// src/oura/newscan/useConnectedFocus.ts
//
// After a Search Console connect started from a line of text (the Review
// health line, the Start step), the Connect button goes away with the line it
// sat in, and the focus it held drops to the page. This hands focus to the new
// status text and says the change once, the way GscCard does on the Website
// step. Only a connect started here moves focus; a page load that finds the
// site already connected says nothing.

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

const SAID = "Search Console connected.";

export interface ConnectedFocus<T extends HTMLElement> {
    /** The status text that shows once connected (give it tabIndex={-1}). */
    targetRef: RefObject<T | null>;
    /** Call from the Connect click. */
    asked(): void;
    /** For a role="status" line that is always rendered. */
    said: string;
}

export function useConnectedFocus<T extends HTMLElement>(connected: boolean): ConnectedFocus<T> {
    const targetRef = useRef<T>(null);
    const pending = useRef(false);
    const [said, setSaid] = useState("");

    useEffect(() => {
        if (!connected || !pending.current) return;
        pending.current = false;
        setSaid(SAID);
        const raf = requestAnimationFrame(() => {
            // Only when the focus was lost with the button, never away from where the staffer moved on to.
            const active = document.activeElement;
            if (!active || active === document.body || !active.isConnected) targetRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(raf);
    }, [connected]);

    const asked = useCallback(() => {
        pending.current = true;
        setSaid("");
    }, []);

    return { targetRef, asked, said };
}
